import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";

// Style
import {
  ResultsGrid,
  PlayerCard,
  PlayerCardTop,
  PlayerCardIdentity,
  PlayerCardDetails,
  PlayerCardName,
  PlayerCardMeta,
  PlayerAvatarImage,
  PlayerAvatar,
  PlayerMetaText,
  PositionBadge,
  PlayerIdentityActionGroup,
  PlayerIdentityActionButton,
  PlayerIdentityNameLink,
  LoadingState,
} from "../../style/SmrtSearch";

// Images
import starOn from "../../images/star_on.svg";
import compareIcon from "../../images/ic_compare.svg";
import compareBlue from "../../images/compare_blue.svg";
import shadowTeamIconDark from "../../images/ic_formation.svg";
import shadowTeamIconLight from "../../images/formation_blue.svg";

// Components
import Empty from "../../components/empty/index";
import Modal from "../../components/modal/index";
import { NotificationList } from "../../components/notification/NotificationList";
import { useNotification } from "../../components/notification/useNotification";

// Language
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { $apiV1, $apiV2 } from "../../config/api";
import { Pagination } from "../ui";
import { Tooltip } from "react-tooltip";
import Button from "../button";
import { useLexicon } from "../../hooks/useLexicon";
import { getMediaUrl } from "../../helpers";
import { getPositionAbbreviation } from "../../helpers/positionAbbreviation";

// Shadow team
import {
  fetchShadowTeams,
  fetchShadowTeamById,
  addShadowTeamPlayer,
} from "../../store/shadowTeam/shadowTeamService";
import {
  trackCompareAction,
  trackFavoriteChange,
  trackShadowTeamAction,
} from "../../tracking";
import {
  buildShadowTeamSlots,
  MAX_SHADOW_TEAM_PLAYERS_PER_SLOT,
  resolveShadowTeamAssignmentsBySlot,
} from "../../pages/shadowTeam/utils/formation";

// ─── Shadow team modal helpers ───────────────────────────────────────────────

const SHADOW_TEAM_BAND_LABELS = {
  gk: "Goalkeeper",
  defense: "Defense",
  defensiveMid: "Defensive Mid",
  midfield: "Midfield",
  attackingMid: "Attacking Mid",
  attack: "Attack",
};

const getShadowTeamSlotGroups = (formation) => {
  if (!Array.isArray(formation?.lines) || formation.lines.length === 0) {
    return [];
  }
  const slots = buildShadowTeamSlots(formation.lines);
  const byLine = {};
  slots.forEach((slot) => {
    const key = slot.lineIndex;
    if (!byLine[key]) {
      byLine[key] = { lineIndex: key, band: slot.band, slots: [] };
    }
    byLine[key].slots.push(slot);
  });
  return Object.values(byLine);
};

const normalizeErrorMessage = (error, fallbackMessage) => {
  const responseMessage = error?.response?.data?.message;
  if (typeof responseMessage === "string" && responseMessage.trim()) {
    return responseMessage;
  }
  return fallbackMessage;
};

// ─── Shadow team modal styled components ─────────────────────────────────────

const ShadowTeamModalShell = styled.div`
  width: min(100%, 520px);
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const ShadowTeamModalTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.white};
  font-family: "SatoshiBold";
  font-size: 24px;
  line-height: 1.2;
  text-align: left;
`;
const ShadowTeamModalSubtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.textMuted};
  font-family: "SatoshiRegular";
  font-size: 13px;
  line-height: 1.5;
  text-align: left;
`;
const ShadowTeamModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: min(56vh, 520px);
  overflow-y: auto;
  padding-right: 4px;
`;
const ShadowTeamModalBack = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.textMuted};
  font-family: "SatoshiMedium";
  font-size: 13px;
  cursor: pointer;
  &:hover { color: ${({ theme }) => theme.white}; }
`;
const ShadowTeamModalStepTitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.textSecondary};
  font-family: "SatoshiBold";
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: left;
`;
const ShadowTeamModalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const ShadowTeamModalItemButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 10px;
  background: ${({ theme }) => theme.cardBgAlt};
  color: ${({ theme }) => theme.white};
  font-family: "SatoshiMedium";
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover:not(:disabled) { border-color: ${({ theme }) => theme.green}; background: ${({ theme }) => theme.accentBg}; }
  &:disabled { cursor: wait; opacity: 0.6; }
`;
const ShadowTeamModalItemText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;
const ShadowTeamModalItemTitle = styled.span`
  color: ${({ theme }) => theme.white};
  font-family: "SatoshiBold";
  font-size: 13px;
  line-height: 1.3;
`;
const ShadowTeamModalItemMeta = styled.span`
  color: ${({ theme }) => theme.textMuted};
  font-family: "SatoshiRegular";
  font-size: 11px;
  line-height: 1.35;
`;
const ShadowTeamModalEmptyState = styled.div`
  padding: 16px 14px;
  border: 1px dashed ${({ theme }) => theme.borderColor};
  border-radius: 12px;
  color: ${({ theme }) => theme.textMuted};
  font-family: "SatoshiRegular";
  font-size: 13px;
  line-height: 1.5;
  text-align: left;
`;
const ShadowTeamSlotSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const ShadowTeamSlotSectionLabel = styled.span`
  color: ${({ theme }) => theme.textMuted};
  font-family: "SatoshiMedium";
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-align: left;
`;
const ShadowTeamSlotGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const ShadowTeamSlotButton = styled.button`
  min-width: 54px;
  padding: 7px 12px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 999px;
  background: ${({ theme }) => theme.cardBgAlt};
  color: ${({ theme }) => theme.textSecondary};
  font-family: "SatoshiMedium";
  font-size: 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
  &:hover:not(:disabled) { border-color: ${({ theme }) => theme.green}; color: ${({ theme }) => theme.green}; }
  &:disabled { cursor: not-allowed; opacity: 0.45; }
`;

// ─── Avatar with fallback ─────────────────────────────────────────────────────

const FavoriteAvatar = ({ player }) => {
  const [failed, setFailed] = useState(false);
  const photo = player?.photos?.length > 0 ? getMediaUrl(player.photos[0].full) : "";

  if (photo && !failed) {
    return (
      <PlayerAvatarImage
        src={photo}
        alt={player?.display_name || ""}
        onError={() => setFailed(true)}
      />
    );
  }

  const initials = (player?.display_name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <PlayerAvatar $background={localStorage.getItem('theme') === 'light' ? '#EFF4FF' : '#1e2a1e'} $color={localStorage.getItem('theme') === 'light' ? '#2363F7' : '#c8f500'}>
      {initials}
    </PlayerAvatar>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const FavoritesTable = ({
  table,
  sortOrder,
  onSortChange,
  page,
  totalPages,
  onPageChange,
  totalPlayers,
}) => {
  const { t } = useTranslation();
  const l = useLexicon();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notifications, addNotification, removeNotification } = useNotification();

  const shadowTeams = useSelector((state) => state.shadowTeam.teams);
  const shadowTeamLoadingTeams = useSelector((state) => state.shadowTeam.loading.teams);
  const shadowTeamAddingPlayer = useSelector((state) => state.shadowTeam.loading.addPlayer);

  const [successPopup, setSuccessPopup] = useState(false);
  const [shadowTeamModalPlayer, setShadowTeamModalPlayer] = useState(null);
  const [shadowTeamSelectedTeamId, setShadowTeamSelectedTeamId] = useState(null);
  const [shadowTeamLoadingTeamId, setShadowTeamLoadingTeamId] = useState(null);

  const shadowTeamModalOpen = Boolean(shadowTeamModalPlayer);
  const shadowTeamModalPlayerName =
    shadowTeamModalPlayer?.name || shadowTeamModalPlayer?.display_name || "";

  const selectedShadowTeam = useMemo(() => {
    const id = Number(shadowTeamSelectedTeamId);
    if (!Number.isFinite(id)) return null;
    return shadowTeams.find((team) => Number(team?.id) === id) || null;
  }, [shadowTeamSelectedTeamId, shadowTeams]);

  const selectedShadowTeamHasLoadedAssignments = Array.isArray(selectedShadowTeam?.assignments);

  const isShadowTeamDetailLoading =
    shadowTeamLoadingTeamId !== null &&
    Number(shadowTeamLoadingTeamId) === Number(shadowTeamSelectedTeamId);

  const selectedShadowTeamSlotPlayers = useMemo(() => {
    if (!selectedShadowTeamHasLoadedAssignments) return {};
    return resolveShadowTeamAssignmentsBySlot(
      selectedShadowTeam?.assignments || [],
      selectedShadowTeam?.formation?.lines || []
    );
  }, [selectedShadowTeam, selectedShadowTeamHasLoadedAssignments]);

  const isShadowTeamSlotFull = useCallback(
    (slot) =>
      (selectedShadowTeamSlotPlayers?.[slot?.key] || []).length >= MAX_SHADOW_TEAM_PLAYERS_PER_SLOT,
    [selectedShadowTeamSlotPlayers]
  );

  const handleCloseShadowTeamModal = useCallback(() => {
    setShadowTeamModalPlayer(null);
    setShadowTeamSelectedTeamId(null);
    setShadowTeamLoadingTeamId(null);
  }, []);

  const handleOpenShadowTeam = useCallback(
    async (player) => {
      setShadowTeamModalPlayer(player);
      setShadowTeamSelectedTeamId(null);
      setShadowTeamLoadingTeamId(null);
      if (shadowTeams.length > 0) return;
      try {
        await dispatch(fetchShadowTeams()).unwrap();
      } catch (error) {
        addNotification(normalizeErrorMessage(error, t("shadowTeam.loadError")), "error");
      }
    },
    [addNotification, dispatch, shadowTeams.length, t]
  );

  const handleSelectShadowTeam = useCallback(
    async (team) => {
      const teamId = Number(team?.id);
      if (!Number.isFinite(teamId)) return;
      setShadowTeamSelectedTeamId(teamId);
      setShadowTeamLoadingTeamId(teamId);
      try {
        await dispatch(fetchShadowTeamById(teamId)).unwrap();
      } catch (error) {
        setShadowTeamSelectedTeamId(null);
        addNotification(normalizeErrorMessage(error, t("shadowTeam.loadError")), "error");
      } finally {
        setShadowTeamLoadingTeamId((v) => (v === teamId ? null : v));
      }
    },
    [addNotification, dispatch, t]
  );

  const handleAddToShadowTeamSlot = useCallback(
    async (slot) => {
      if (!shadowTeamModalPlayer || !selectedShadowTeam || isShadowTeamSlotFull(slot)) return;
      try {
        await dispatch(
          addShadowTeamPlayer({
            shadowTeamId: selectedShadowTeam.id,
            playerId: Number(shadowTeamModalPlayer.id),
            positionGroup: slot.positionGroup,
            positionOrder: slot.positionOrder,
          })
        ).unwrap();
        trackShadowTeamAction({
          action: "player_add",
          metadata: {
            position_group: slot.positionGroup,
            position_order: slot.positionOrder,
            slot_key: slot.key,
          },
          playerId: Number(shadowTeamModalPlayer?.id),
          playerName: shadowTeamModalPlayerName,
          shadowTeamId: Number(selectedShadowTeam?.id),
          shadowTeamName: selectedShadowTeam?.name || null,
          sourceSurface: "favorites",
        });
        addNotification(
          t("smrtSearch.shadowTeamAddedMessage", {
            name: shadowTeamModalPlayerName,
            team: selectedShadowTeam.name,
            slot: slot.shortLabel,
          }),
          "success"
        );
        handleCloseShadowTeamModal();
      } catch (error) {
        addNotification(
          normalizeErrorMessage(error, t("smrtSearch.shadowTeamErrorMessage")),
          "error"
        );
      }
    },
    [
      addNotification,
      dispatch,
      handleCloseShadowTeamModal,
      isShadowTeamSlotFull,
      selectedShadowTeam,
      shadowTeamModalPlayer,
      shadowTeamModalPlayerName,
      t,
    ]
  );

  const handleRemoveFromFavorites = async (item) => {
    try {
      await $apiV1.delete(`/users/favorites/players/${item?.player_id}/`);
      trackFavoriteChange({
        favoriteStateAfter: false,
        favoriteStateBefore: true,
        metadata: {
          source_surface: "favorites",
        },
        playerId: item?.player_id,
        playerName: item?.player?.display_name || null,
        sourceSurface: "favorites",
      });
      window.location.reload();
    } catch (error) {
      console.error("Error removing from favorites:", error);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage);
    }
  };

  const handleCompare = async (item) => {
    const playerId = parseInt(item.player_id, 10);
    try {
      await $apiV2.post(`/platform/compare/add`, { player_id: playerId });
      trackCompareAction({
        action: "add",
        metadata: {
          source_surface: "favorites",
        },
        playerId,
        playerName: item?.player?.display_name || null,
        sourceSurface: "favorites",
      });
      const storedPlayers = localStorage.getItem("players");
      let playersArray = storedPlayers ? JSON.parse(storedPlayers) : [];
      if (!playersArray.includes(playerId)) {
        playersArray.push(playerId);
        localStorage.setItem("players", JSON.stringify(playersArray));
      }
      setSuccessPopup(true);
    } catch (error) {
      console.error("Error adding player to compare list:", error);
    }
  };

  return (
    <>
      {table?.length > 0 ? (
        <>
          <ResultsGrid>
            {table.map((item, index) => {
              const player = item.player;
              const playerName = player?.display_name || "";
              const positionCode = getPositionAbbreviation(player?.position);
              const teamName = player?.team?.name || player?.current_team?.name || "";
              const teamId = player?.team?.id || player?.current_team?.id || null;
              const nationName = player?.nationality?.[0]?.name || player?.nation?.name || "";

              return (
                <PlayerCard key={item.player_id || index}>
                  <PlayerCardTop>
                    <PlayerCardIdentity>
                      <FavoriteAvatar player={player} />

                      <PlayerCardDetails>
                        <PlayerCardName>
                          <PlayerIdentityNameLink
                            href={`/tournaments/player/${item.player_id}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {playerName}
                          </PlayerIdentityNameLink>
                        </PlayerCardName>

                        <PlayerIdentityActionGroup>
                          <PlayerIdentityActionButton
                            type="button"
                            aria-label={t("compareWorkspace.removeFavorite")}
                            data-tooltip-id={`fav-remove-${item.player_id}`}
                            onClick={() => handleRemoveFromFavorites(item)}
                          >
                            <img src={starOn} alt="" aria-hidden="true" />
                          </PlayerIdentityActionButton>
                          <Tooltip id={`fav-remove-${item.player_id}`} place="bottom" content={t("compareWorkspace.removeFavorite")} />

                          <PlayerIdentityActionButton
                            type="button"
                            aria-label={t("smrtSearch.comparePlayerAction")}
                            data-tooltip-id={`fav-compare-${item.player_id}`}
                            onClick={() => handleCompare(item)}
                          >
                            <img
                              src={localStorage.getItem("theme") === "light" ? compareBlue : compareIcon}
                              alt=""
                              aria-hidden="true"
                            />
                          </PlayerIdentityActionButton>
                          <Tooltip id={`fav-compare-${item.player_id}`} place="bottom" content={t("smrtSearch.comparePlayerAction")} />

                          <PlayerIdentityActionButton
                            type="button"
                            aria-label={t("smrtSearch.shadowTeamPlayerAction")}
                            data-tooltip-id={`fav-shadow-${item.player_id}`}
                            onClick={() =>
                              handleOpenShadowTeam({
                                id: item.player_id,
                                name: playerName,
                              })
                            }
                          >
                            <img
                              src={localStorage.getItem("theme") === "light" ? shadowTeamIconLight : shadowTeamIconDark}
                              alt=""
                              aria-hidden="true"
                            />
                          </PlayerIdentityActionButton>
                          <Tooltip id={`fav-shadow-${item.player_id}`} place="bottom" content={t("smrtSearch.shadowTeamPlayerAction")} />
                        </PlayerIdentityActionGroup>

                        <PlayerCardMeta>
                          {item.flag ? (
                            <img
                              src={item.flag}
                              alt={nationName}
                              style={{ width: 16, height: 11, objectFit: "cover", borderRadius: 2 }}
                              onError={(e) => { e.target.style.display = "none"; }}
                            />
                          ) : null}
                          {nationName ? <PlayerMetaText>{nationName}</PlayerMetaText> : null}
                          {teamName ? (
                            teamId ? (
                              <PlayerIdentityNameLink
                                href={`/tournaments/team/${teamId}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {teamName}
                              </PlayerIdentityNameLink>
                            ) : (
                              <PlayerMetaText>{teamName}</PlayerMetaText>
                            )
                          ) : null}
                          {positionCode && positionCode !== "-" ? (
                            <PositionBadge $position={positionCode}>
                              {positionCode}
                            </PositionBadge>
                          ) : null}
                        </PlayerCardMeta>
                      </PlayerCardDetails>
                    </PlayerCardIdentity>
                  </PlayerCardTop>
                </PlayerCard>
              );
            })}
          </ResultsGrid>

          <Pagination
            styleWrapper={{ marginTop: 16 }}
            items={totalPlayers}
            itemsPerPage={10}
            page={page - 1 || 0}
            onPageChange={handlePageChange}
          />
        </>
      ) : (
        <Empty title={l(447)} text={l(1052)} button1={l(1053)} button2={l(1054)} />
      )}

      {/* Compare success modal */}
      <Modal isOpen={successPopup} setIsOpen={setSuccessPopup} closeIc>
        <h1>{l(1046)}</h1>
        <br />
        <Button
          title={l(1047)}
          size={"full"}
          onClick={() => navigate(`/compare`)}
          onContextMenu={() => window.open("/compare")}
        />
      </Modal>

      {/* Shadow team modal */}
      <Modal
        isOpen={shadowTeamModalOpen}
        setIsOpen={handleCloseShadowTeamModal}
        closeIc
        width="560px"
        mobileWidth="calc(100vw - 24px)"
        mobilePadding="72px 0 28px"
        padding="withClose"
        unstyledTypography
      >
        <ShadowTeamModalShell>
          <ShadowTeamModalTitle>{t("smrtSearch.shadowTeamModalTitle")}</ShadowTeamModalTitle>
          <ShadowTeamModalSubtitle>
            {t("smrtSearch.shadowTeamModalSubtitle", { name: shadowTeamModalPlayerName })}
          </ShadowTeamModalSubtitle>

          <ShadowTeamModalBody>
            {!selectedShadowTeam ? (
              <>
                <ShadowTeamModalStepTitle>
                  {t("smrtSearch.shadowTeamSelectTeamStep")}
                </ShadowTeamModalStepTitle>
                {shadowTeamLoadingTeams ? (
                  <LoadingState>{t("compareWorkspace.loading")}</LoadingState>
                ) : shadowTeams.length > 0 ? (
                  <ShadowTeamModalSection>
                    {shadowTeams.map((team) => (
                      <ShadowTeamModalItemButton
                        key={team.id}
                        type="button"
                        onClick={() => handleSelectShadowTeam(team)}
                      >
                        <ShadowTeamModalItemText>
                          <ShadowTeamModalItemTitle>{team.name}</ShadowTeamModalItemTitle>
                          <ShadowTeamModalItemMeta>
                            {team.formationName || team.formation?.name || ""}
                          </ShadowTeamModalItemMeta>
                        </ShadowTeamModalItemText>
                        <span aria-hidden="true">›</span>
                      </ShadowTeamModalItemButton>
                    ))}
                  </ShadowTeamModalSection>
                ) : (
                  <ShadowTeamModalEmptyState>
                    {t("smrtSearch.shadowTeamNoTeamsMessage")}
                  </ShadowTeamModalEmptyState>
                )}
              </>
            ) : (
              <>
                <ShadowTeamModalBack
                  type="button"
                  onClick={() => {
                    setShadowTeamSelectedTeamId(null);
                    setShadowTeamLoadingTeamId(null);
                  }}
                >
                  ‹ {t("smrtSearch.shadowTeamBack")}
                </ShadowTeamModalBack>

                <ShadowTeamModalSection>
                  <ShadowTeamModalStepTitle>
                    {t("smrtSearch.shadowTeamSelectPositionStep")}
                  </ShadowTeamModalStepTitle>
                  <ShadowTeamModalItemMeta>
                    {selectedShadowTeam.name}
                    {selectedShadowTeam.formationName || selectedShadowTeam.formation?.name
                      ? ` · ${selectedShadowTeam.formationName || selectedShadowTeam.formation?.name}`
                      : ""}
                  </ShadowTeamModalItemMeta>
                </ShadowTeamModalSection>

                {isShadowTeamDetailLoading ? (
                  <LoadingState>{t("compareWorkspace.loading")}</LoadingState>
                ) : null}

                {getShadowTeamSlotGroups(selectedShadowTeam.formation).map((group) => (
                  <ShadowTeamSlotSection key={group.lineIndex}>
                    <ShadowTeamSlotSectionLabel>
                      {SHADOW_TEAM_BAND_LABELS[group.band] || group.band}
                    </ShadowTeamSlotSectionLabel>
                    <ShadowTeamSlotGrid>
                      {group.slots.map((slot) => (
                        <ShadowTeamSlotButton
                          key={slot.key}
                          type="button"
                          disabled={
                            shadowTeamAddingPlayer ||
                            isShadowTeamDetailLoading ||
                            !selectedShadowTeamHasLoadedAssignments ||
                            isShadowTeamSlotFull(slot)
                          }
                          onClick={() => handleAddToShadowTeamSlot(slot)}
                        >
                          {slot.shortLabel}
                        </ShadowTeamSlotButton>
                      ))}
                    </ShadowTeamSlotGrid>
                  </ShadowTeamSlotSection>
                ))}
              </>
            )}
          </ShadowTeamModalBody>
        </ShadowTeamModalShell>
      </Modal>

      <NotificationList notifications={notifications} onClose={removeNotification} />
    </>
  );
};

export default FavoritesTable;
